// Geo-data hook (useGeoData) and LAG_INFO, used by the Slides "Streaming Geo" slide (src/Slides/StreamingGeo.tsx).
import { useMemo, useState, useEffect } from "react";
import * as R from "ramda";
import { useSetError } from "../redux/modals";

import { ToolsLambdaFetch, awaitJSON } from "../utils/fetch-utils";
import { DMACode, GeoDataMap, GeoData, GeoDataObject } from "../Components/DmaMap";

import { makeHeatMap } from "../utils/colors";

import * as S from "@blisspointmedia/bpm-types/dist/StreamingPerformance";

const IMPRESSIONS_CUTOFF_PERCENTAGE = 0.0005;
// Defines the range of captured values in terms of the standard deviation
const COLOR_RANGE_SENSITIVITY = 2;
const DISABLED_COLOR = "#F2F2F2";
const MID_COLOR = "#FFFFFF";
const HIGH_COLOR = "#00AA47";

type Lag = string;
export const LAG_INFO: Record<Lag, { index: number; name: string }> = {
  "1d": { index: 0, name: "1 Day" },
  "3d": { index: 1, name: "3 Days" },
  "7d": { index: 2, name: "7 Days" },
  "14d": { index: 3, name: "14 Days" },
  "30d": { index: 4, name: "30 Days" },
  all_business: { index: 5, name: "Overall Business" },
};

interface DateRange {
  startDate: string;
  endDate: string;
}

export const useGeoData = (
  dateRange: DateRange,
  prefix: S.Prefix,
  kpi?: string,
  lag?: string
): {
  geoData?: GeoDataObject;
  renderColor?: (dma: DMACode) => string;
} => {
  const setError = useSetError();

  const dataKey = useMemo(
    () => `${dateRange.startDate}_${dateRange.endDate}_${kpi}_${lag}_${prefix}`,
    [kpi, lag, dateRange, prefix]
  );

  const [geoDataMap, setGeoDataMap] = useState<Record<string, GeoDataObject>>({});
  const geoData = useMemo(() => geoDataMap[dataKey], [geoDataMap, dataKey]);

  useEffect(() => {
    if (R.isNil(geoData) && kpi && lag) {
      setGeoDataMap(map => ({
        ...map,
        [dataKey]: false,
      }));
      (async () => {
        try {
          let geoData = await ToolsLambdaFetch("/geo", {
            params: { kpi, lag, prefix, ...dateRange },
          });
          let res = await awaitJSON(geoData);
          setGeoDataMap(map => ({ ...map, [dataKey]: res }));
        } catch (e) {
          const reportError = e as Error;
          setError({
            message: `Failed to get geo data: ${reportError.message}`,
            reportError,
          });
        }
      })();
    }
  }, [geoData, kpi, lag, dataKey, dateRange, setError, prefix]);

  const heatMapData = useMemo(() => {
    if (geoData) {
      const totalImpressions = R.pipe<GeoDataMap, GeoData[], number>(
        R.values,
        R.reduce((impressions, dmaData) => impressions + dmaData.impressions, 0)
      )(geoData);

      const impressionsCutoff = totalImpressions * IMPRESSIONS_CUTOFF_PERCENTAGE;

      const geoIndices: number[] = [];
      for (let dmaData of R.values(geoData)) {
        if (dmaData.impressions > impressionsCutoff) {
          geoIndices.push(dmaData.index);
        }
      }

      const mean = R.reduce(R.add, 0, geoIndices) / geoIndices.length;

      const std =
        (R.reduce((acc, geoIndex) => acc + (geoIndex - mean) ** 2, 0, geoIndices) /
          geoIndices.length) **
        (1 / 2);

      return {
        min: mean - COLOR_RANGE_SENSITIVITY * std,
        max: mean + COLOR_RANGE_SENSITIVITY * std,
        impressionsCutoff,
      };
    }
  }, [geoData]);

  const renderColor = useMemo(() => {
    if (geoData) {
      return (dma: DMACode) => {
        if (heatMapData && geoData && geoData[dma]) {
          const { index, impressions } = geoData[dma];
          const { min, max, impressionsCutoff } = heatMapData;

          if (impressions < impressionsCutoff) {
            return DISABLED_COLOR;
          }

          let heatMap = makeHeatMap({
            min,
            max,
            midColor: MID_COLOR,
            highColor: HIGH_COLOR,
          });
          return heatMap(index);
        }
        return "transparent";
      };
    }
  }, [heatMapData, geoData]);

  return {
    geoData,
    renderColor,
  };
};
